import { useState, useRef, useEffect } from "react";
import { X, ChevronDown, AlignLeft, Trash2 } from "lucide-react";
import api from "../../connector/api";
import PremiumDateRangePicker from "../../components/PremiumDateRangePicker";

export default function LeaveFormModal({ isOpen, onClose, onLeaveSubmitted, selectedLeave }) {
  const [formData, setFormData] = useState({
    leave_type: "Personal Leave",
    start_date: "",
    end_date: "",
    reason: "",
  });
  const [loading, setLoading] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const isEditMode = !!selectedLeave;
  // Safety guard: locked once processed by management
  const isLocked = selectedLeave?.status && selectedLeave.status !== "PENDING";

  const leaveOptions = [
    "Personal Leave",
    "Compassionate Leave",
    "Emergency Leave",
    "Leave Without Pay",
    "Sick Leave"
  ];

  // Sync modal state whenever it's opened or swapped
  useEffect(() => {
    if (isOpen) {
      if (selectedLeave) {
        setFormData({
          leave_type: selectedLeave.leave_type || "Personal Leave",
          start_date: selectedLeave.start_date || "",
          end_date: selectedLeave.end_date || "",
          reason: selectedLeave.reason || "",
        });
      } else {
        // Reset defaults for clean entries
        setFormData({
          leave_type: "Personal Leave",
          start_date: "",
          end_date: "",
          reason: "",
        });
      }
    }
  }, [isOpen, selectedLeave]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleDateChange = (field, value) => {
    if (isLocked) return;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isLocked) return;

    if (!formData.start_date || !formData.end_date) {
      alert("Validation Error: Please select both a Start Date and an End Date.");
      return;
    }

    if (!formData.reason.trim()) {
      alert("Validation Error: Please provide a reason for your absence request.");
      return;
    }

    setLoading(true);
    try {
      let res;
      if (isEditMode) {
        res = await api.put(`leaves/${selectedLeave.id}/`, formData);
      } else {
        // Just send the normal API request. Django will take care of the notification!
        res = await api.post("leaves/", formData);
      }

      window.dispatchEvent(new Event("localLeaveUpdate"));
      onLeaveSubmitted(res.data);
      onClose();
    } catch (err) {
      console.error("Backend processing validation failure:", err.response?.data);
      const serverErrors = err.response?.data ? JSON.stringify(err.response.data) : err.message;
      alert(`Submission Rejected:\n${serverErrors}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!isEditMode || isLocked) return;
    
    setLoading(true);
    try {
      await api.delete(`leaves/${selectedLeave.id}/`);
      
      // Broadcast the sync event on deletion as well
      window.dispatchEvent(new Event("localLeaveUpdate"));

      // 📢 Pass an explicit object or safe identifier back to clear the parent tracking state
      onLeaveSubmitted({ id: selectedLeave.id, deleted: true }); 
      onClose();
    } catch (err) {
      console.error("Delete operation rejected:", err.response?.data);
      alert("Failed to discard entry record. It may have already been processed.");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center font-dynamic-title p-4 bg-slate-900/40 backdrop-blur-xs transition-opacity">
      
      <div className="bg-white border border-slate-200 w-full max-w-md p-6 rounded-xl shadow-xl relative flex flex-col space-y-5">
        <div className="absolute top-0 left-6 right-6 h-[2px] bg-gradient-to-r from-transparent via-[#002855]/30 to-transparent" />
        {/* Modal Header */}
        <div className="flex items-center justify-between border-b border-slate-100 pb-3">
          <div>
            <h3 className="text-sm font-bold text-slate-900 tracking-tight">
              {isEditMode ? (isLocked ? "View Leave Details" : "Edit Leave Request") : "Apply for Leave"}
            </h3>
            <p className="text-[10px] text-slate-400 font-medium mt-0.5">
              {isLocked ? "This record has been processed and is locked." : "Log or modify a formal time-off entry."}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-7 h-7 rounded-lg border border-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-50 transition"
          >
            <X size={14} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          
          {/* Custom Dropdown */}
          <div className="space-y-1.5" ref={dropdownRef}>
            <label className="text-[11px] font-bold tracking-wide text-slate-400 uppercase">Leave Type</label>
            <div className="relative">
              <button
                type="button"
                disabled={isLocked}
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="w-full h-9 bg-white border border-slate-200 rounded-xl px-3 text-xs font-semibold text-slate-800 flex items-center justify-between shadow-3xs hover:border-slate-300 focus:border-[#002855] focus:ring-4 focus:ring-[#002855]/5 outline-none transition disabled:bg-slate-50 disabled:text-slate-400"
              >
                <span>{formData.leave_type}</span>
                {!isLocked && <ChevronDown size={14} className={`text-slate-400 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />}
              </button>

              {isDropdownOpen && !isLocked && (
                <div className="absolute left-0 right-0 mt-1.5 bg-white border border-slate-200 rounded-xl shadow-lg py-1.5 z-50 max-h-48 overflow-y-auto">
                  {leaveOptions.map((option) => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => {
                        setFormData({ ...formData, leave_type: option });
                        setIsDropdownOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-xs font-medium transition ${
                        formData.leave_type === option  
                          ? "bg-[#002855]/5 text-[#002855] font-bold"  
                          : "text-slate-600 hover:bg-slate-50"
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <PremiumDateRangePicker 
            startDate={formData.start_date}
            endDate={formData.end_date}
            onChange={handleDateChange}
            disabled={isLocked}
          />

          {/* Reason Block */}
          <div className="space-y-1.5">
            <label className="text-[11px] font-bold tracking-wide text-slate-400 uppercase">Reason for Absence</label>
            <div className="relative">
              <AlignLeft className="absolute left-3 top-2.5 w-3.5 h-3.5 text-slate-400" />
              <textarea
                rows={3}
                disabled={isLocked}
                placeholder="Provide details or context regarding this request..."
                className="w-full bg-white border border-slate-200 rounded-xl pl-9 pr-3 py-2 text-xs font-medium text-slate-800 placeholder-slate-400 focus:border-[#002855] focus:ring-4 focus:ring-[#002855]/5 outline-none transition resize-none shadow-3xs disabled:bg-slate-50 disabled:text-slate-400"
                value={formData.reason}
                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                required
              />
            </div>
          </div>

          {/* Actions Footer Layout */}
          <div className="flex justify-between items-center pt-3 border-t border-slate-100 gap-2">
            <div>
              {/* 🎯 DELETE OPTION: Only visible during edits when the request is still pending */}
              {isEditMode && !isLocked && (
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={loading}
                  className="h-9 px-3 border border-rose-200 text-rose-600 bg-rose-50/50 hover:bg-rose-50 rounded-xl font-bold text-xs transition flex items-center gap-1.5 shadow-3xs"
                >
                  <Trash2 size={13} />
                  <span>Withdraw</span>
                </button>
              )}
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                className="h-9 px-4 border border-slate-200 hover:bg-slate-50 text-slate-600 rounded-xl font-bold text-xs transition"
              >
                {isLocked ? "Close" : "Cancel"}
              </button>
              
              {!isLocked && (
                <button
                  type="submit"
                  disabled={loading}
                  className="h-9 px-5 bg-[#002855] bg-opacity-80 text-white hover:bg-[#001f42] rounded-xl font-bold text-xs transition shadow-xs disabled:opacity-50"
                >
                  {loading ? "Processing..." : isEditMode ? "Save Modifications" : "Submit Leave Request"}
                </button>
              )}
            </div>
          </div>

        </form>
      </div>
    </div>
  );
}